import React, { useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { StateSelect, US_STATES } from "../../components/fields/StateSelect";
import { PlusCodeMap } from "../../components/fields/PlusCodeMap";
import { AddressAutocomplete } from "../../components/fields/AddressAutocomplete";
import type { PlaceDetails } from "@/shared/api/location/geoPlacesClient";
// @ts-expect-error no types
import { OpenLocationCode } from "open-location-code";

const olc = new OpenLocationCode();

export interface StationFormData {
  name: string;
  city: string;
  state: string;
  zip_code: string;
  county: string;
  address: string;
  contact_email: string;
  contact_number: string;
  rms_used: string;
  bodycam_vendor: string;
  seat_limit: string;
  subscription_tier: string;
  service_area_plus_code: string;
}

export type FormErrors = Partial<Record<keyof StationFormData, string>>;

type AddressDerivedField =
  | "address"
  | "city"
  | "state"
  | "zip_code"
  | "county";

export const EMPTY_FORM: StationFormData = {
  name: "",
  city: "",
  state: "",
  zip_code: "",
  county: "",
  address: "",
  contact_email: "",
  contact_number: "",
  rms_used: "",
  bodycam_vendor: "",
  seat_limit: "",
  subscription_tier: "",
  service_area_plus_code: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-(). ]{7,20}$/;
const PLUS_CODE_RE = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,7}$/i;

export function validateForm(data: StationFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.name.trim()) errors.name = "Required";
  if (!data.address.trim()) errors.address = "Required";
  if (!data.city.trim()) errors.city = "Required";
  if (!data.county.trim()) errors.county = "Required";
  if (!data.zip_code.trim()) errors.zip_code = "Required";

  if (!data.state.trim()) {
    errors.state = "Required";
  } else if (!US_STATES.includes(data.state.toUpperCase())) {
    errors.state = "Must be a valid US state abbreviation (e.g. CA, TX, DC)";
  }

  if (data.contact_email.trim() && !EMAIL_RE.test(data.contact_email)) {
    errors.contact_email = "Must be a valid email address";
  }

  if (data.contact_number.trim() && !PHONE_RE.test(data.contact_number)) {
    errors.contact_number = "Must be a valid phone number";
  }

  if (!data.bodycam_vendor) errors.bodycam_vendor = "Required";
  if (!data.subscription_tier) errors.subscription_tier = "Required";

  const seats = parseInt(data.seat_limit, 10);
  if (!data.seat_limit.trim()) {
    errors.seat_limit = "Required";
  } else if (isNaN(seats) || seats <= 0) {
    errors.seat_limit = "Must be a positive number";
  }

  if (!data.service_area_plus_code.trim()) {
    errors.service_area_plus_code =
      "Required — select an address suggestion to calculate it";
  } else if (!PLUS_CODE_RE.test(data.service_area_plus_code)) {
    errors.service_area_plus_code =
      "Must be a valid Plus Code (e.g. 8FVC9G8F+6W)";
  }

  return errors;
}

export interface StationFormProps {
  formData: StationFormData;
  formErrors: FormErrors;
  handleFieldChange: (field: keyof StationFormData, value: string) => void;
  disabled: boolean;
  textFieldSx?: {
    "& .MuiOutlinedInput-root"?: { borderRadius?: number | string };
  };
}

export const StationForm: React.FC<StationFormProps> = ({
  formData,
  formErrors,
  handleFieldChange,
  disabled,
  textFieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
}) => {
  const [plusCodeError, setPlusCodeError] = useState<string | null>(null);

  const handleAddressDerivedFieldChange = (
    field: AddressDerivedField,
    value: string,
  ) => {
    const changed = formData[field] !== value;
    handleFieldChange(field, value);

    // The Plus Code is derived from the coordinates of the selected address.
    // Once any displayed address component changes, the old code would point
    // at a location the form no longer describes.
    if (changed && formData.service_area_plus_code) {
      handleFieldChange("service_area_plus_code", "");
      setPlusCodeError(
        "Address changed. Select a suggestion to recalculate the map location.",
      );
    }
  };

  // When the user picks an address suggestion, auto-fill the address
  // components and derive the service-area Plus Code from its coordinates.
  const handleAddressSelect = (details: PlaceDetails) => {
    handleFieldChange("address", details.address);
    if (details.city) handleFieldChange("city", details.city);
    if (details.state) handleFieldChange("state", details.state.toUpperCase());
    if (details.zip) handleFieldChange("zip_code", details.zip);
    if (details.county) handleFieldChange("county", details.county);
    const plusCode: string = olc.encode(details.lat, details.lon, 10);
    handleFieldChange("service_area_plus_code", plusCode);
    setPlusCodeError(null);
  };

  return (
    <Grid container spacing={2.5} sx={{ pt: 1 }}>
      {/* Station Name */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Station Name"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          disabled={disabled}
          error={!!formErrors.name}
          helperText={formErrors.name}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* Address */}
      <Grid size={{ xs: 12 }}>
        <AddressAutocomplete
          required
          value={formData.address}
          onInputChange={(text) =>
            handleAddressDerivedFieldChange("address", text)
          }
          onSelect={handleAddressSelect}
          onSearchError={() =>
            setPlusCodeError(
              "Address search is unavailable. Try again or verify the address-search configuration.",
            )
          }
          onLookupError={() =>
            setPlusCodeError(
              "Could not look up that address. Please verify the fields and select a suggestion.",
            )
          }
          disabled={disabled}
          error={!!formErrors.address}
          helperText={
            formErrors.address ||
            "Start typing and select a suggestion to auto-fill the address"
          }
          sx={textFieldSx}
        />
      </Grid>

      {/* City | State */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="City"
          required
          fullWidth
          variant="outlined"
          value={formData.city}
          onChange={(e) =>
            handleAddressDerivedFieldChange("city", e.target.value)
          }
          disabled={disabled}
          error={!!formErrors.city}
          helperText={formErrors.city}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <StateSelect
          required
          value={formData.state}
          onChange={(newValue) =>
            handleAddressDerivedFieldChange("state", newValue)
          }
          disabled={disabled}
          error={!!formErrors.state}
          helperText={formErrors.state}
          sx={textFieldSx}
        />
      </Grid>

      {/* Zip Code | County */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Zip Code"
          required
          fullWidth
          variant="outlined"
          value={formData.zip_code}
          onChange={(e) =>
            handleAddressDerivedFieldChange("zip_code", e.target.value)
          }
          disabled={disabled}
          error={!!formErrors.zip_code}
          helperText={formErrors.zip_code}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="County"
          required
          fullWidth
          variant="outlined"
          value={formData.county}
          onChange={(e) =>
            handleAddressDerivedFieldChange("county", e.target.value)
          }
          disabled={disabled}
          error={!!formErrors.county}
          helperText={formErrors.county}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* Contact Email | Contact Number */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Contact Email"
          fullWidth
          variant="outlined"
          value={formData.contact_email}
          onChange={(e) => handleFieldChange("contact_email", e.target.value)}
          disabled={disabled}
          error={!!formErrors.contact_email}
          helperText={formErrors.contact_email}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Contact Number (optional)"
          fullWidth
          variant="outlined"
          value={formData.contact_number}
          onChange={(e) => handleFieldChange("contact_number", e.target.value)}
          disabled={disabled}
          error={!!formErrors.contact_number}
          helperText={formErrors.contact_number}
          sx={textFieldSx}
          
        />
      </Grid>

      {/* RMS Used | Bodycam Vendor */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth error={!!formErrors.rms_used}>
          <InputLabel>RMS Used</InputLabel>
          <Select
            label="RMS Used"
            value={formData.rms_used}
            onChange={(e) => handleFieldChange("rms_used", e.target.value)}
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="EF">Eforce</MenuItem>
            <MenuItem value="CS">Central Square</MenuItem>
            <MenuItem value="SP">Spillman</MenuItem>
            <MenuItem value="SR">SunRidge</MenuItem>
            <MenuItem value="TN">TrueNorth</MenuItem>
          </Select>
          {formErrors.rms_used && (
            <FormHelperText>{formErrors.rms_used}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth error={!!formErrors.bodycam_vendor}>
          <InputLabel>Bodycam Vendor</InputLabel>
          <Select
            label="Bodycam Vendor"
            value={formData.bodycam_vendor}
            onChange={(e) =>
              handleFieldChange("bodycam_vendor", e.target.value)
            }
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="KARDA">None</MenuItem>
            <MenuItem value="AXON">AXON</MenuItem>
            <MenuItem value="LENSLOCK">LENSLOCK</MenuItem>
            <MenuItem value="WATCHGUARD">WATCHGUARD</MenuItem>
          </Select>
          {formErrors.bodycam_vendor && (
            <FormHelperText>{formErrors.bodycam_vendor}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Seat Limit | Subscription Tier */}
      <Grid size={{ xs: 12, sm: 2 }}>
        <TextField
          label="Seat Limit"
          fullWidth
          variant="outlined"
          type="number"
          value={formData.seat_limit}
          onChange={(e) => handleFieldChange("seat_limit", e.target.value)}
          disabled={disabled}
          error={!!formErrors.seat_limit}
          helperText={formErrors.seat_limit}
          inputProps={{ min: 1 }}
          sx={textFieldSx}
          
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth error={!!formErrors.subscription_tier}>
          <InputLabel>Subscription</InputLabel>
          <Select
            label="Subscription"
            value={formData.subscription_tier}
            onChange={(e) =>
              handleFieldChange("subscription_tier", e.target.value)
            }
            disabled={disabled}
            sx={{
              borderRadius:
                textFieldSx?.["& .MuiOutlinedInput-root"]?.borderRadius ?? 0,
            }}
          >
            <MenuItem value="TRIAL">TRIAL</MenuItem>
            <MenuItem value="PRO">PRO</MenuItem>
          </Select>
          {formErrors.subscription_tier && (
            <FormHelperText>{formErrors.subscription_tier}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Service Area Plus Code */}
      <Grid size={{ xs: 6 }}>
        <TextField
          label="Service Area Plus Code"
          fullWidth
          variant="outlined"
          value={formData.service_area_plus_code}
          disabled={disabled}
          error={!!formErrors.service_area_plus_code || !!plusCodeError}
          helperText={
            formErrors.service_area_plus_code ||
            plusCodeError ||
            "Automatically calculated from the selected address"
          }
          placeholder="e.g. 8FVC9G8F+6W"
          sx={textFieldSx}
          InputProps={{ readOnly: true }}
        />
      </Grid>

      {/* Service Area Map Preview */}
      {formData.service_area_plus_code && (
        <Grid size={{ xs: 12 }}>
          <PlusCodeMap plusCode={formData.service_area_plus_code} />
        </Grid>
      )}
    </Grid>
  );
};
